import {Fancybox} from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';
import {IFileModel} from '../../@interfaces/models/FIle/IFileModel';
import config from '../../helpers/config';
import {IFilesViewer} from '../../@interfaces/services/FilesViewer/IFilesViewer';
import {FilesViewerItem} from '../../@types/services/FilesViewer/FilesViewerItem';
import {slideType} from '@fancyapps/ui/types/Carousel/types';
import {MakeRequired} from '../../@types/RequireOne';
import {IFilesViewerEvents} from '../../@interfaces/services/FilesViewer/IFilesViewerEvents';
import {IListener} from '../../@interfaces/services/Events';
import {ISubscription} from '../../@interfaces/services/Events/ISubscription';
import app from '../../helpers/app';
import {FilesViewerOptions} from '../../@types/services/FilesViewer/FilesViewerOptions';

export default class FilesViewer implements IFilesViewer {
    protected objectUrlsToRevoke: string[] = [];

    constructor() {
        Fancybox.defaults.on = {
            'done': () => {
                this.events.dispatch(IFilesViewerEvents.OPENED, {viewer: this});
            },
            'close': () => {
                this.revokeObjectsUrls();
                this.events.dispatch(IFilesViewerEvents.CLOSED, {viewer: this});
            },
            'Carousel.settle': () => {
                this.events.dispatch(IFilesViewerEvents.SLIDE_OPENED, {viewer: this});
            },
        };
    }

    open(files: FilesViewerItem | FilesViewerItem[], options?: FilesViewerOptions) {
        const items = this.prepareItems(files);
        if (items.length) {
            this.close();
            Fancybox.show(items, this.prepareOptions(options));
        }

        return this;
    }

    bind(container: HTMLElement, selector: string, options?: FilesViewerOptions) {
        Fancybox.bind(container, selector, this.prepareOptions(options));
        this.events.dispatch(IFilesViewerEvents.BONDED, {viewer: this});

        return this;
    }

    unbind(container: HTMLElement, selector: string) {
        Fancybox.unbind(container, selector);
        this.events.dispatch(IFilesViewerEvents.UNBOUNDED, {viewer: this});

        return this;
    }

    close() {
        Fancybox.close(true);

        return this;
    }

    openSlide(index: number) {
        Fancybox.getInstance()?.jumpTo(index);

        return this;
    }

    isAllowedItem(item: FilesViewerItem): boolean {
        const was = this.objectUrlsToRevoke;
        this.objectUrlsToRevoke = [];
        const items = this.prepareItems(item);
        this.revokeObjectsUrls();
        this.objectUrlsToRevoke = was;

        return items.length > 0;
    }

    on(event: IFilesViewerEvents | IFilesViewerEvents[], listener: IListener<{ viewer: IFilesViewer }>): ISubscription {
        return this.events.listen(event, listener);
    }

    protected revokeObjectsUrls() {
        this.objectUrlsToRevoke.forEach(url => URL.revokeObjectURL(url));
        this.objectUrlsToRevoke = [];
    }

    protected prepareOptions(options?: FilesViewerOptions): FilesViewerOptions {
        return {
            Hash: false,
            ...(options ?? {}),
            Thumbs: {
                type: 'modern',
                ...(options?.Thumbs ?? {}),
            },
            Toolbar: {
                display: {
                    left: ['infobar'],
                    middle: [
                        'zoomIn',
                        'zoomOut',
                        'toggle1to1',
                        'rotateCCW',
                        'rotateCW',
                        'flipX',
                        'flipY',
                    ],
                    right: ['slideshow', 'download', 'thumbs', 'close'],
                    ...(typeof options?.Toolbar === 'object' ? (options?.Toolbar?.display ?? {}) : {}),
                },
                ...(options?.Toolbar ?? {}),
            },
        };
    }

    protected prepareItems(files: FilesViewerItem | FilesViewerItem[]): Partial<slideType>[] {
        const items = Array.isArray(files) ? files : [files];

        return items.reduce<Partial<slideType>[]>((res, item) => {
            if (item instanceof File) {
                if (this.isAllowedFile({file_size: 1, file_mime_type: item.type})) {
                    res.push({src: this.createObjectUrl(item)});
                }
            } else if (typeof item === 'string') {
                res.push({src: item, downloadSrc: item});
            } else if (item.asset_file_path && this.isAllowedFile(item)) {
                res.push({src: item.asset_file_path, downloadSrc: item.asset_file_path});
            }

            return res;
        }, []);
    }

    protected createObjectUrl(file: File): string {
        const url = URL.createObjectURL(file);
        this.objectUrlsToRevoke.push(url);

        return url;
    }

    protected isAllowedFile(file: MakeRequired<IFileModel, 'file_size' | 'file_mime_type'>): boolean {
        const maxFileMbToView = config('client.files-viewer.max-file-size-mb', null);

        return !file.file_size ||
            maxFileMbToView == null ||
            !file.file_mime_type ||
            !(
                file.file_mime_type.startsWith('image/') ||
                file.file_mime_type === 'application/pdf'
            ) ||
            this.bytesToMegabytes(file.file_size) <= maxFileMbToView;
    }

    protected bytesToMegabytes(bytes: number): number {
        return bytes / 1024 / 1024;
    }

    protected get events() {
        return app('events');
    }
}
